import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {useDispatch, useSelector} from "react-redux";
import CompanyHeader from "components/CompanyHeader";
import CompanySideMenu from "components/CompanySideMenu";
import { useSnackbar } from "notistack";
import CustomPagination from "components/CustomPagination";
import ReturnButton from "../../components/ReturnButton";
import CustomButton from "../../components/CustomButton";
import Copyright from "../../components/Copyright";
import TextInput from "../../components/TextInput";
import Dropdown from "../../components/Dropdown";
import CustomDatePicker from "../../components/CustomDatePicker";
import { bulkMessagesActions, getListBuldMessage, getBuldMessageDetail, pauseBuldMessage,
  setCurrentPage, extendSessionTimeOut
 } from "reducers/bulkMessages";
import { setJobSelected } from "reducers/chatMessage";
import {formatDateTime, dateToString, trimStringFields, getPeriodicTaskStatus} from "utils/format";
import BulkScoutingDialog from "./components/BulkScoutingDialog";
import CustomToast from "../../components/CustomToast";
import { InfoIcon } from "../../components/Icons";
import { SCHEDULE_CHAT_STATUS, BULK_MESSAGE_SESSION_MINUTE_EXTEND, STAFF_TYPE, PERIODIC_TASK_STATUS } from '../../constants/index';

const initTableHeaders = [
  { title: "送信日時", isSort: true, currentSortState: 1, minWidth: 184, field: "public_time" },
  { title: "ステータス", isSort: true, currentSortState: 0, minWidth: 168, field: "status__label" },
  { title: "求人", isSort: true, currentSortState: 0, minWidth: null, field: "job__title" },
  { title: "件数", isSort: true, currentSortState: 0, minWidth: 104, field: "talent_count" },
  { title: "種別", isSort: true, currentSortState: 0, minWidth: 160, field: "chat_type__label" }
];
const sortingStates = ["default", "desc", "asc"];

const pageSizeOptions = [
  {
    id: 10,
    name: "10件を表示",
  },
  {
    id: 50,
    name: "50件を表示",
  },
  {
    id: 100,
    name: "100件を表示",
  },
];

let startDate = "";
let endDate = "";

const BulkMessageList = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {staffInfo} = useSelector((state: any) => state.currentUser);
  const { bulkingMessageList, currentPage, countItems, actionStatus, actionName,
    resMessage, isSuccess,
   } = useSelector((state: any) => state.bulkMessages);
  const [tableHeaders, setTableHeaders] = useState(initTableHeaders);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>('-public_time');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [bulkScoutingOpen, setBulkScoutingOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [pageSize, setPageSize] = useState({ id: 10, name: "10件を表示" });
    

  const fetchListBuldMessage = (page=currentPage, searchQueryInput=searchQuery) => {
      let data = {
          search: searchQueryInput ? searchQueryInput.trim(): '',
          page: page,
          pageSize: pageSize?.id ? pageSize?.id : 10,
          sortBy: sortBy,
          sortOrder: sortOrder,
          startDate: dateToString(dateRange.startDate),
          endDate: dateToString(dateRange.endDate),
      }
      data = trimStringFields(data);
      dispatch(getListBuldMessage(data));
  }

  useEffect(() => {
    fetchListBuldMessage();
  }, [sortBy, sortOrder, currentPage, dateRange]);

  useEffect(() => {
      const pageNumber = 1;
      if (actionStatus === 'fulfilled') {
        switch(actionName) {
          case 'pauseBuldMessage':
            showCustomToast({message: resMessage, variant: isSuccess ? "success" : "error"});
            fetchListBuldMessage();
            setSelectedRows([]);
            break;
          case 'saveBuldMessage':
            if (pageNumber == currentPage) {
              fetchListBuldMessage();
            }
            else {
              handleChangePage(pageNumber);
            }
            break;
          case 'updateBuldMessage':
            fetchListBuldMessage(pageNumber);
            handleChangePage(pageNumber);
            break;
          default:
            break;
        }
      }
  }, [actionStatus, actionName]);

  const showCustomToast = ({title="メッセージ", message, variant="success", type="normal"}) => {
    enqueueSnackbar("I love snacks.", {
      content: (key) => (
        <CustomToast
          title={title}
          message={message}
          variant={variant}
          snackbarKey={key}
          closeSnackbar={closeSnackbar}
          type={type}
        />
      ),
    });
  }

  const handlePauseBulkMessage = () => {
    if (selectedRows.length == 0) {
      return
    }

    dispatch(pauseBuldMessage({requestBody: {ids: selectedRows}}));
  };

  const handleSelectAll = () => {
    if (selectedRows.length == 0) {
      const newSelectedRows = bulkingMessageList
        .filter((n: any) => n?.status?.value == SCHEDULE_CHAT_STATUS.ON_GOING)
        .map((n: any) => n.id);
      setSelectedRows(newSelectedRows);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id: any) => {
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: any = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }
    setSelectedRows(newSelected);
  };

  const handleChangePage = (pageNumber: any) => {
      dispatch(bulkMessagesActions.setCurrentPage(pageNumber));
  };

  const handleChangePageSize = (value: any) => {
      setPageSize(value)
      fetchListBuldMessage(1);
  };

  const handleSort = (index: any) => {
      const newHeaders = tableHeaders.map((header, idx) => (index === idx)
      ? ({...header, currentSortState: (header.currentSortState + 1) % sortingStates.length})
      : ({...header, currentSortState: 0}));
      setTableHeaders(newHeaders);

      let newSortBy = "";
      for (let item of newHeaders) {
        if(item.currentSortState != 0) {
          let refix_str = item.currentSortState == 1 ? "-" : "";
          newSortBy += refix_str + item.field;
          break
        }
      }
      setSortBy(newSortBy);
  }

  const handleEditBulkMessage = (id: any, viewDetail: boolean = false) => {
    dispatch(extendSessionTimeOut({min: BULK_MESSAGE_SESSION_MINUTE_EXTEND}));

    // Get bulk message detail
    dispatch(getBuldMessageDetail({id: id}));

    // Show modal edit
    setBulkScoutingOpen(true)
    if(viewDetail) dispatch(bulkMessagesActions.setViewDetail(true));
  }

  const handeSearchBulkMessage = (event: any) => {
      if (event.key === "Enter") {
          const page = 1;
          dispatch(bulkMessagesActions.setCurrentPage(page));
          fetchListBuldMessage(page);
      }
  };

  const handeClearSearchQuery = () => {
    setSearchQuery("")
    fetchListBuldMessage(1, '')
  };

  const getMinDate = () => {
    if(dateRange.startDate) {
      return dateRange.startDate
    }
    return new Date(2000, new Date().getMonth(), new Date().getDate())
  };

  const getMaxDate = () => {
    if(dateRange.endDate) {
      return dateRange.endDate
    }
    return new Date(9999, new Date().getMonth(), new Date().getDate())
  };

  return (
    <>
      <CompanyHeader />
      <div className="mt-6 px-6 flex gap-6 relative">
        <CompanySideMenu />
        <div className="flex-1">
          <div className="mb-6 flex items-center gap-4">
            <ReturnButton onClick={() => navigate('/company/talent', { state: { isFromDetail: true } })}/>
            <span className="text-sm alu-text-base-1">/</span>
            <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">一括送信メッセージ</h3>
          </div>
          <div className="rounded-t-lg px-4 py-2 bg-white border-b alu-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <CustomButton
                  type="button"
                  size="sm"
                  onClick={handlePauseBulkMessage}
                  disabled={selectedRows.length === 0 || staffInfo?.type == STAFF_TYPE.GENERAL}
                >
                  送信停止
                </CustomButton>
                <div className="flex items-center gap-2">
                  <div className="w-[10rem]">
                    <CustomDatePicker
                      value={dateRange.startDate}
                      onChange={(value: any) => setDateRange((prev: any) => ({ ...prev, startDate: value }))}
                      placeholderText={""}
                      customStyles="!h-8"
                      maxDate={getMaxDate()}
                    />
                  </div>
                  <span className="alu-text-base-1">-</span>
                  <div className="w-[10rem]">
                    <CustomDatePicker
                      value={dateRange.endDate}
                      onChange={(value: any) => setDateRange((prev: any) => ({ ...prev, endDate: value }))}
                      placeholderText={""}
                      customStyles="!h-8"
                      minDate={getMinDate()}
                    />
                  </div>
                </div>
                <div className="w-[9.25rem]">
                  <Dropdown
                    size="filter"
                    inputWidth="full"
                    value={pageSize}
                    options={pageSizeOptions}
                    placeholder="選択してください"
                    onChange={(value: any) => handleChangePageSize(value)}
                  />
                </div>
              </div>
              <div className='relative'>
                <TextInput
                  value={searchQuery}
                  onChange={(e:any) => setSearchQuery(e.target.value)}
                  onClearQuery={handeClearSearchQuery}
                  onKeyDown={(e:any) => handeSearchBulkMessage(e)}
                  placeholder="検索"
                  searchIcon={true}
                  customClassName="!h-[28px] !w-[240px] !text-xs"
                  size={"filter"}
                />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg mb-6">
            <table className="table-auto w-full">
              <thead className="border-b alu-border">
              <tr>
                <th className="w-[3.25rem] pl-4 pr-3 py-4 text-left">
                  <div className="flex items-center">
                    <button onClick={handleSelectAll} disabled={staffInfo?.type == STAFF_TYPE.GENERAL}>
                      {selectedRows.length == 0 ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <g clipPath="url(#clip0_8574_230)"><path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill={staffInfo?.type == STAFF_TYPE.GENERAL ? "#BBB" : "#808080"}/></g>
                          <defs><clipPath id="clip0_8574_230"><rect width="24" height="24" fill="white"/></clipPath></defs>
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <g clipPath="url(#clip0_8574_236)"><path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#FF861A"/></g>
                          <defs><clipPath id="clip0_8574_236"><rect width="24" height="24" fill="white"/></clipPath></defs>
                        </svg>
                      )}
                    </button>
                  </div>
                </th>
                {tableHeaders.map((header, idx) => (
                  <th key={idx} className={`px-3 py-4 text-left ${header.minWidth ? `w-[${header.minWidth}px]` : "w-auto"}`}>
                    <div onClick={() => header.isSort && handleSort(idx)}
                         className={`flex items-center ${header.isSort ? 'hover:cursor-pointer' : ''}`}>
                      <p
                        className={`${sortBy.includes(header?.field) ? 'font-bold underline underline-offset-4 alu-text-base-2' : 'font-normal alu-text-base-1'} text-sm leading-[1.3125]`}>
                        {header.title}
                      </p>
                      {header.isSort && (
                        <button className={`${header.currentSortState === 2 ? 'rotate-180' : 'rotate-0'}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                               viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_8574_176)">
                              <path d="M7 10L12 15L17 10H7Z" fill="#808080"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_8574_176">
                                <rect width="24" height="24" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </button>)}
                    </div>
                  </th>
                ))}
              </tr>
              </thead>
              <tbody>
              {bulkingMessageList?.map((item: any, idx: any) => (
                <tr key={idx} className={`bg-white hover:bg-[#FFF3E5] border-b border-b-[#E0E0E0]`}>
                  <td className="w-[3.25rem] pl-4 pr-3 py-4 text-left font-normal text-sm leading-[1.3125]">
                    <div className="flex items-center">
                      <button onClick={(e:any) => {handleSelectRow(item?.id);e.stopPropagation()}} disabled={item?.status?.value != SCHEDULE_CHAT_STATUS.ON_GOING || staffInfo?.type == STAFF_TYPE.GENERAL}>
                        {!selectedRows.includes(item?.id) ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_8574_230)"><path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill={item?.status?.value != SCHEDULE_CHAT_STATUS.ON_GOING || staffInfo?.type == STAFF_TYPE.GENERAL ? "#BBB" : "#808080"}/></g>
                            <defs><clipPath id="clip0_8574_230"><rect width="24" height="24" fill="white"/></clipPath></defs>
                          </svg>
                        ) : (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><g clipPath="url(#clip0_8574_236)"><path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.11 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#FF861A"/></g><defs><clipPath id="clip0_8574_236"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>)}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-left font-normal text-sm leading-[1.3125] w-[184px]">
                    <p className="underline underline-offset-2 hover:text-[#FF861A] hover:cursor-pointer alu-text-base-2"
                      onClick={() => item?.status?.value == SCHEDULE_CHAT_STATUS.COMPLETE ? handleEditBulkMessage(item?.id, true) : handleEditBulkMessage(item?.id)}>
                      {formatDateTime(item?.public_time)}
                    </p>
                    {item?.periodic_task_status && item?.periodic_task_status?.status == PERIODIC_TASK_STATUS.EXPIRED && (
                      <div className="mt-1 flex gap-1">
                        <span className="mt-[2px]"><InfoIcon /> </span>
                        <span className="alu-text-base-2 text-xs font-normal alu-text-error whitespace-pre-wrap">{getPeriodicTaskStatus(item?.periodic_task_status)}</span>
                      </div>
                    )}
                  </td>
                  <td 
                    onMouseEnter={(e: any) => e.currentTarget.parentElement.classList.remove("hover:bg-[#FFF3E5]")}
                    onMouseLeave={(e: any) => e.currentTarget.parentElement.classList.add("hover:bg-[#FFF3E5]")}
                    className="px-3 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[168px]">
                      <div
                      className="w-[143px] flex items-center px-2 py-[0.375rem] rounded bg-[#F7F7F7] gap-1 leading-[21px]">
                        { item?.status?.value != SCHEDULE_CHAT_STATUS.COMPLETE &&
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                          >
                            <circle cx="6" cy="6" r="6" fill={item?.status?.value == SCHEDULE_CHAT_STATUS.ON_GOING ? "#06C270" : "#FFC700"} />
                          </svg>
                        }
                      <span className="ml-1">{item?.status?.label}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-auto">
                    { item?.job ?
                      <p className="underline underline-offset-2 hover:text-[#FF861A] hover:cursor-pointer" 
                      onClick={() => navigate("/company/job/detail/" + item?.job?.id)}>
                        {item?.job?.title ? item?.job?.title : 'ー'}
                      </p>
                      :
                      "ー"
                    }
                    
                  </td>
                  <td className="px-3 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[104px]">
                    {item?.talent_count}
                  </td>
                  <td className="px-3 py-4 text-left font-normal text-sm leading-[1.3125] alu-text-base-2 w-[160px]">
                    {item?.chat_type?.label ? item?.chat_type?.label : 'メッセージ'}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          {bulkingMessageList?.length > 0 && (<div className="flex items-center justify-center">
            <CustomPagination 
                total={countItems} 
                currentPage={currentPage}
                pageSize={pageSize?.id} 
                onPageChange={handleChangePage} 
                shape="circle"
            />
          </div>)}
        </div>
        {/* {bulkScoutingOpen && ( */}
            <BulkScoutingDialog open={bulkScoutingOpen} onClose={() => setBulkScoutingOpen(false)} />
        {/* )} */}
      </div>
      <Copyright view="company" />
    </>
  );
};

export default BulkMessageList;
